import "../../../common/polyfills.ts";
import type { ReactNode } from "react";
import { createRoot } from "react-dom/client";
import api from "./api/index.ts";
import { Controller } from "./components/Controller/index.tsx";
import router, { MATCHING_ROUTE_NOT_FOUND } from "./router/index.ts";
import type { Env } from "../../../common/types.ts";
import { ERROR_MESSAGE_UNDEFINED_SEASON } from "../../../common/constants.ts";
import { EmptyState } from "./components/EmptyState.tsx";
import { LeagueNotFoundMessage } from "./components/LeagueNotFoundMessage.tsx";
import { ErrorBoundary } from "./components/ErrorBoundary.tsx";
import { genStaticPage } from "./util/genStaticPage.tsx";
import { promiseWorker } from "./util/promiseWorker.ts";
import { routes } from "./util/routes.ts";
import { safeLocalStorage } from "./util/safeLocalStorage.ts";
import { logEvent } from "./util/logEvent.ts";
import { setupThemeSync } from "./util/themeSync.ts";
import { toWorker } from "./util/toWorker.ts";
import { initReactI18next } from "react-i18next";
import i18next, { initI18n } from "./util/i18n.ts";
import { installFeedbackTierPreview } from "./util/previewFeedbackTier.ts";
window.bbgm = { api, logEvent, toWorker };

// Dev-only: attaches window.bbgm.__previewFeedbackTier for on-device L2/L3
// preview. No-op / stripped in production builds.
installFeedbackTierPreview();

const hasApiHandler = (name: string): name is keyof typeof api =>
	Object.hasOwn(api, name);

const invokeApiHandler = (name: keyof typeof api, params: unknown[]) => {
	const handler = api[name] as (...args: unknown[]) => unknown;
	return handler(...params);
};

const handleVersion = async () => {
	const cleanupThemeSync = setupThemeSync();
	window.addEventListener("pagehide", cleanupThemeSync, { once: true });

	window.withGoodUI?.();

	toWorker("main", "ping", undefined).then(() => {
		window.withGoodWorker?.();
	});

	// Store the running version for future diagnostics (e.g. logging if a
	// downgrade ever happens). No conflict-detection on mobile.
	safeLocalStorage.setItem("bbgmVersion", window.bbgmVersion);
};

const setupEnv = async () => {
	const env: Env = {
		bbgmVersion: window.bbgmVersion,
		mobile: window.mobile,
	};
	await toWorker("main", "init", env);
};

const setupMobileSelectBlur = () => {
	if (!window.mobile) {
		return;
	}

	document.addEventListener(
		"change",
		(event) => {
			const target = event.target;
			if (!(target instanceof HTMLSelectElement)) {
				return;
			}

			// iOS WKWebView can leave native selects focused after the picker
			// closes, which blocks the next tap from reopening the picker.
			window.setTimeout(() => {
				if (document.activeElement === target) {
					target.blur();
				}
			}, 0);
		},
		true,
	);
};

const render = () => {
	const container = document.getElementById("content");
	const root = createRoot(container!);
	root.render(
		<ErrorBoundary>
			<Controller />
		</ErrorBoundary>,
	);
};

const setupRoutes = async () => {
	let initialLoad = true;
	await router.start({
		routeMatched: async ({ context }) => {
			if (!context.state.noTrack && initialLoad) {
				initialLoad = false;
			}
		},
		navigationEnd: ({ context, error }) => {
			if (error) {
				let errorMessage: ReactNode;

				// Some "errors" are expected and need either some special UI or are not worth logging
				if (error.message === MATCHING_ROUTE_NOT_FOUND) {
					errorMessage = (
						<EmptyState
							title={i18next.t("errorMessage.pageNotFoundTitle", {
								ns: "ui",
								defaultValue: "Page not found",
							})}
						>
							{i18next.t("errorMessage.pageNotFoundBody", {
								ns: "ui",
								defaultValue:
									"This address does not match any page in the game. If you got here from an in-game link, please report it as a bug.",
							})}
						</EmptyState>
					);
				} else if (error.message === "League not found.") {
					errorMessage = <LeagueNotFoundMessage />;
				} else if (
					// As of 2019-07-20, these cover all IndexedDB version error messages in Chrome, Firefox, and Safari
					error.message.includes("requested version") ||
					error.message.includes("existing version") ||
					error.message.includes("higher version") ||
					error.message.includes("version requested") ||
					error.message.includes("lower version")
				) {
					errorMessage = (
						<>
							<p>{error.message}</p>
							<p>
								Please make sure you have the latest version of the game loaded.
							</p>
						</>
					);
				} else if (error.message === ERROR_MESSAGE_UNDEFINED_SEASON) {
					errorMessage = error.message;
				} else {
					// Log any error not explicitly handled above
					console.error("Error from view:");
					console.error(error);

					errorMessage = error.message;
				}

				const ErrorPage = (
					<>
						{typeof errorMessage === "string" ? (
							<p>{errorMessage}</p>
						) : (
							errorMessage
						)}
					</>
				);
				const errorPage = genStaticPage(
					"error",
					i18next.t("common.error", { ns: "ui", defaultValue: "Error" }),
					ErrorPage,
					context.path.startsWith("/l/"),
				);
				errorPage(context);
			}
		},
		routes,
	});
};

(async () => {
	// Initialize i18n before rendering so all components get correct language.
	// initReactI18next must be added before init() is called.
	i18next.use(initReactI18next);
	const saved = safeLocalStorage.getItem("language");
	const lang = saved ?? (navigator.language.startsWith("zh") ? "zh" : "en");
	await initI18n(lang);
	setupMobileSelectBlur();

	promiseWorker.register(([name, ...params]) => {
		if (!hasApiHandler(name)) {
			throw new Error(
				`API call to nonexistant UI function "${name}" with params ${JSON.stringify(
					params,
				)}`,
			);
		}

		return invokeApiHandler(name, params);
	});
	await handleVersion();
	await setupEnv();
	render();
	await setupRoutes();
})();
